import React, {useCallback, useEffect, useMemo, useState} from 'react'
import styled from 'styled-components'
import {
  GIFT_SHOP_PACKAGE_MAX_PRICE,
  GIFT_SHOP_PACKAGE_MIN_PRICE,
  REGEX_NUMBER,
} from 'consts'
import {useTranslation} from 'i18n'
import {
  GiftShopTemplateModalCredential,
  GiftShopUploadPriceLimitModal,
} from 'pages'
import {AuthCredentialType, TransactionCurrencyData} from 'types'
import {useDidMount, useHistory} from 'utils'
import {
  DropdownItem,
  Input,
  Label,
  List,
  ListItemSelect,
  Menu,
} from 'common/components'
import {VALIDATION_GIFTSHOP_UPLOAD_REQUIRED_PRICE} from 'common/validations'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'
import {GiftShopSettingPackageFormPriceProps} from './GiftShopSettingPackageFormPriceProps'

const StyledPriceContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-content: flex-start;
  margin-top: ${convertUnit(25)};
`

const StyledCurrencyDropdownContainer = styled.div`
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  flex: 1;
`

export default function GiftShopSettingPackageFormPrice({
  currencyName,
  form,
  stateInputPrice,
  currencyData,
}: GiftShopSettingPackageFormPriceProps) {
  const history = useHistory()
  const user = useSelector('user')
  const {translate} = useTranslation()
  const {watch, setValue} = form
  const {price} = watch()
  const [inputPrice, setInputPrice] = stateInputPrice
  const currencyStateToggle = useState(false)
  const [toggle, setToggle] = currencyStateToggle
  const [modal, setModal] = useState(false)
  const credential = useMemo<AuthCredentialType | undefined>(
    () => (!user?.email ? 'email' : !user?.phone_number ? 'phone' : undefined),
    [user?.email, user?.phone_number],
  )
  const stateModalCredential = useState(false)

  const maximum_price = GIFT_SHOP_PACKAGE_MAX_PRICE
  const minimum_price = GIFT_SHOP_PACKAGE_MIN_PRICE

  const handleSelectCurrency = useCallback(
    (item: TransactionCurrencyData) => {
      setValue('currencyName', item.currency)
      setValue('currencyId', item.id)
    },
    [setValue],
  )

  const handleRenderCurrencyDropdownItem = useCallback(
    (item: TransactionCurrencyData) => (
      <ListItemSelect
        title={item.currency}
        onClick={() => handleSelectCurrency(item)}
      />
    ),
    [handleSelectCurrency],
  )

  const handleRenderPriceLimitModal = useMemo(
    () => (
      <GiftShopUploadPriceLimitModal
        visible={modal}
        isUpload
        toggleModal={() => setModal((prev) => !prev)}
        continueAction={() => {
          credential
            ? stateModalCredential[1](true)
            : history.push('giftshop_upgrade_creator', {})
        }}
      />
    ),
    [credential, history, modal, stateModalCredential],
  )

  const handleNavigateAddCredential = useCallback(() => {
    stateModalCredential[1](false)
    history.push('giftshop_edit_profile', {
      menu: credential === 'email' ? 'email' : 'phone',
    })
  }, [credential, history, stateModalCredential])

  const handleRenderCredentialModal = useMemo(
    () => (
      <GiftShopTemplateModalCredential
        stateModal={stateModalCredential}
        credential={credential}
        onAgree={handleNavigateAddCredential}
      />
    ),
    [credential, handleNavigateAddCredential, stateModalCredential],
  )

  useEffect(() => {
    if (price.toString().startsWith('0')) {
      setValue('price', '0')
      setInputPrice('0')
    }
    if (
      price.length >= 2 &&
      price.toString().charAt(0) === '0' &&
      price.toString().charAt(1) !== '0'
    ) {
      setValue('price', price.toString().charAt(1))
      setInputPrice(price.toString().charAt(1))
    }
    if (inputPrice === '') {
      setInputPrice('0')
    }
  }, [inputPrice, price, setInputPrice, setValue])

  useDidMount(() => {
    setInputPrice(price.toString());
  });

  return (
    <>
      <StyledPriceContainer>
        {handleRenderPriceLimitModal}
        {handleRenderCredentialModal}
        <Label
          label={translate('global:currency')}
          containerStyle={{
            flex: 1,
            maxWidth: convertUnit(200),
            height: convertUnit(27.5),
          }}>
          <StyledCurrencyDropdownContainer>
            <Menu
              stateToggle={currencyStateToggle}
              contentContainerStyle={{
                height: convertUnit(96),
                borderRadius: convertUnit(8),
              }}
              renderContent={
                <List
                  data={currencyData}
                  keyExtractor={(item) => item.id}
                  renderItem={handleRenderCurrencyDropdownItem}
                />
              }>
              <DropdownItem
                label={currencyName}
                toggle={toggle}
                onClick={(e) => {
                  e.preventDefault()
                  setToggle((previous) => !previous)
                }}
              />
            </Menu>
          </StyledCurrencyDropdownContainer>
        </Label>
        <Input
          name="price"
          type="number"
          min={minimum_price}
          max={maximum_price}
          defaultValue={price}
          form={form}
          formRules={VALIDATION_GIFTSHOP_UPLOAD_REQUIRED_PRICE(
            minimum_price,
            maximum_price,
          )}
          label={translate('global:basicPrice')}
          allowedCharacters={REGEX_NUMBER}
          autoComplete={false}
          placeholder={translate('giftShop:uploadInputPricePlaceholder')}
          containerStyle={{
            marginLeft: '25px',
            flex: 1,
          }}
          rightIcon="delete"
          onRightIconClick={() => {
            setInputPrice('0')
          }}
          onChangeText={setInputPrice}
        />
      </StyledPriceContainer>
    </>
  )
}
